<template>
  <d-modal
    size="lg"
    @close="handleClose">
    <d-modal-header>
      <d-modal-title>
        {{ $t('welcome.searchProducts') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body class="px-3">
      <d-row>
        <d-col
          :class="{ 'founded-products': true, 'is-loading': !loaded }"
          cols="8">
          <d-row>
            <d-col
              v-for="(product, index) of foundedProducts"
              :key="'search-result-product-' + index"
              cols="6">
              <d-card class="w-100 p-3 mb-4">
                <img
                  v-if="product.image_url"
                  :src="product.image_url"
                  alt=""
                  class="product-image" />
                <span
                  v-else
                  class="product-empty-image">
                  <i class="material-icons">camera_alt</i>
                </span>
                <d-badge
                  v-if="product.is_sale"
                  theme="warning"
                  class="mt-1 mb-2">
                  {{ $t('price.isSale') }}
                </d-badge>
                <div class="text-center">
                  {{ product.name }}<br>
                  <small>{{ product.code }}</small><br>
                  <small :class="{ 'text-danger': product.availability <= 0, 'text-warning': product.availability < 100 && product.availability > 0, 'text-success': product.availability >= 100 }">
                    {{ $t('ui.availability') }}: {{ product.availability }} {{ $t('ui.piecesAbbr') }}
                  </small>
                  <br>
                  <a
                    href="#"
                    class="deliveries-link"
                    @click.prevent="showContainerDeliveries(product)">
                    <i class="material-icons">local_shipping</i>
                    {{ $t('container.deliveries') }}
                  </a>
                  <br>
                  <template v-if="showAllCurrencies">
                    <div
                      v-for="(price, priceIndex) of product.prices"
                      :key="'price-item-' + index + '-' + priceIndex">
                      <strong class="text-primary">
                        {{ Number(price.price).toFixed(2) + ' ' }}
                        <template v-if="currencyNames[price.currency_id]">
                          {{ currencyNames[price.currency_id] }}
                        </template>
                      </strong>
                    </div>
                  </template>
                  <template v-else>
                    <strong v-if="!productsDiscount || !product.include_discounts" class="text-primary">
                      {{ getProductPrice(product) }}
                    </strong>

                    <del
                      v-if="productsDiscount > 0 && product.include_discounts"
                      style="display: block; line-height: 1;">
                      <small>
                        {{ getProductPrice(product) }}
                      </small>
                    </del>

                    <strong v-if="productsDiscount > 0 && product.include_discounts" class="text-primary">
                      {{ getProductDiscountedPrice(product) }}
                    </strong>
                  </template>
                </div>

                <div
                  v-if="cartEnabled"
                  class="d-flex mt-2">
                  <d-input-group
                    :append="$t('ui.piecesAbbr')"
                    class="w-50">
                    <d-input
                      v-model="product.qty"
                      type="number"
                      class="w-100 text-right"
                      @focus.native="selectText" />
                  </d-input-group>

                  <d-button
                    class="w-50 ml-1"
                    :outline="!isInCart(product.id)"
                    @click.prevent="addProduct(product.id, product.qty)">
                    <template v-if="isInCart(product.id)">
                      {{ $t('ui.inCart') }} ({{ countInCart(product.id) }})
                    </template>
                    <template v-else>
                      {{ $t('ui.addToCart') }}
                    </template>
                  </d-button>
                </div>

                <d-button
                  v-if="!cartEnabled"
                  class="w-100 mt-2"
                  @click.prevent="addProductToOrder(product.id)">
                  {{ $t('ui.add') }}
                </d-button>
              </d-card>
            </d-col>
          </d-row>

          <d-alert
            v-if="!foundedProducts || !foundedProducts.length"
            theme="warning"
            show>
            {{ $t('welcome.noProductsInSearchCriteria') }}
          </d-alert>

          <pagination
            v-if="foundedProducts && foundedProducts.length"
            :meta-data="paginationMetaData"
            :initial-page="currentPage" />
        </d-col>

        <d-col
          tag="form" @keydown.enter.prevent
          cols="4"
          class="filter-sidebar py-0 px-0">

          <d-button-group
            v-if="cartEnabled"
            size="small"
            class="popup-tabs ml-5 mr-5 w-75">
            <d-button
              theme="primary"
              :outline="activeTab !== 'filters'"
              @click.prevent="activeTab = 'filters'">
              {{ $t('ui.filtersSearch') }}
            </d-button>
            <d-button
              theme="primary"
              :outline="activeTab !== 'cart'"
              @click.prevent="activeTab = 'cart'">
              {{ $t('ui.cart') }} ({{ cart.length }})
            </d-button>
          </d-button-group>

          <div
            v-if="cartEnabled && activeTab === 'cart'"
            class="cart-wrapper px-3">
            <d-alert
              v-if="!cart.length"
              show
              theme="warning"
              class="text-center">
              {{ $t('ui.cartIsEmpty') }}
            </d-alert>

            <ul
              v-if="cart.length"
              class="cart-content">
              <li
                v-for="(product, index) of cart"
                :key="'product-in-cart-' + index"
                class="cart-content-item d-flex">
                <img
                  :src="product.product_image_url"
                  width="80"
                  height="80"
                  style="object-fit: contain;"
                  alt="" />

                <div class="ml-2 w-75">
                  <small class="cart-content-item-name d-block w-100">
                    {{ product.product_name }}
                  </small>

                  <div class="cart-content-item-actions d-flex mt-2">
                    <small>
                      &times; {{ product.qty }} {{ $t('ui.piecesAbbr') }}
                    </small>

                    <span
                      class="text-danger ml-2"
                      :title="$t('ui.delete')"
                      @click="deleteFromCart(index)">
                      <i class="material-icons">delete</i>
                    </span>
                  </div>
                </div>
              </li>
            </ul>
          </div>

          <div
            v-if="!cartEnabled || activeTab === 'filters'"
            class="filters-values-wrapper px-3">
            <div class="filters-values">
              <div class="form-group">
                <d-checkbox
                  v-model="filters.is_sale"
                  class="w-100"
                  toggle
                  toggle-small
                  @click.native="onEnter">
                  {{ $t('price.isSale') }}
                </d-checkbox>
              </div>

              <div class="form-group">
                <label>{{ $t('welcome.orderBy') }}:</label>

                <div class="d-flex">
                  <d-form-select
                    v-model="filters.order_by"
                    class="mr-2 w-50"
                    @change="onEnter">
                    <option value="price">{{ $t('ui.sorting.price') }}</option>
                    <option value="code">{{ $t('ui.sorting.code') }}</option>
                    <option value="availability">{{ $t('ui.sorting.availability') }}</option>
                  </d-form-select>

                  <d-form-select
                    v-model="filters.order_dir"
                    class="w-50"
                    @change="onEnter">
                    <option value="ASC">{{ $t('ui.sorting.asc') }}</option>
                    <option value="DESC">{{ $t('ui.sorting.desc') }}</option>
                  </d-form-select>
                </div>
              </div>

              <div class="form-group">
                <label>{{ $t('welcome.filterByNameOrCode') }}:</label>
                <d-input
                  v-model="filters.search"
                  class="w-100"
                  @focus.native="selectText"
                  v-on:keyup.native.enter="onEnter" />
              </div>

              <div class="form-group">
                <label>{{ $t('welcome.filterAvailability') }}:</label>
                <d-input-group
                  :append="$t('ui.piecesAbbr')">
                  <d-input
                    v-model="filters.availability_from"
                    class="w-100"
                    @focus.native="selectText"
                    v-on:keyup.native.enter="onEnter" />
                </d-input-group>
              </div>

              <div class="form-group">
                <label>{{ $t('welcome.filterByPrice') }}:</label>
                <d-row class="pl-3 pr-3 d-flex flex-nowrap">
                  <input
                    :class="{
                      'w-50': true,
                      'form-control': true,
                      'is-invalid': filters.price_to && (filters.price_form < 0 || parseFloat(filters.price_to) < parseFloat(filters.price_from))
                    }"
                    :invalid="filters.price_to && (filters.price_form < 0 || parseFloat(filters.price_to) < parseFloat(filters.price_from)) ? true : null"
                    type="number"
                    v-model="filters.price_from"
                    :placeholder="$t('price.priceFrom')"
                    v-on:keyup.enter="onEnter"
                    v-on:focus="selectText"
                    :min="0" />
                  <span class="px-2 pt-1">-</span>
                  <input
                    :class="{
                      'form-control': true,
                      'w-50': true,
                      'is-invalid': filters.price_to && filters.price_form < 0
                    }"
                    :invalid="filters.price_to && filters.price_form < 0 ? true : null"
                    type="number"
                    v-model="filters.price_to"
                    :placeholder="$t('price.priceTo')"
                    v-on:keyup.enter="onEnter"
                    v-on:focus="selectText"
                    :min="0" />
                </d-row>
              </div>

              <div
                key="main-filter"
                class="form-group">
                <label class="d-flex">
                  <strong>{{ $t('settlements.type') }}</strong>
                </label>

                <div class="form-control is-select">
                  <v-select
                    :options="mainTags"
                    v-model="filters.main_tag"
                    :reduce="tag => tag.id"
                    @input="resetTagFiltersWithoutMain"
                    :placeholder="$t('welcome.choose')"
                    label="name">
                  </v-select>
                </div>
              </div>

              <div
                v-for="(filterType, index) of filteredTagCategories"
                :key="'filter-type-' + index"
                class="form-group">
                <label class="d-flex">
                  <span>{{ filterType.name }}</span>
                  <small class="ml-auto">
                    <d-form-checkbox
                      :key="'filter-type-checkbox-' + index"
                      inline
                      v-model="filters.tagOperators[filterType.id]"
                      :value="true">
                      {{ $t('welcome.requireAll') }}
                    </d-form-checkbox>
                  </small>
                </label>

                <div class="form-control is-select">
                  <v-select
                    :options="filterType.tags"
                    v-model="filters.tags[filterType.id]"
                    :reduce="tag => tag.id"
                    :placeholder="$t('welcome.choose')"
                    :multiple="true"
                    label="name">
                  </v-select>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="cartEnabled && activeTab === 'cart'"
            class="filters-actions pb-3">
            <d-button
              @click.prevent="addToOrder">
              {{ $t('ui.createOrderFromCart') }}
            </d-button>

            <d-button
              theme="danger"
              outline
              @click.prevent="resetCart"
              class="ml-2">
              {{ $t('welcome.clear') }}
            </d-button>
          </div>

          <div
            v-if="!cartEnabled || activeTab === 'filters'"
            class="filters-actions pb-3">
            <d-button
              @click.prevent="loadFilteredData">
              {{ $t('welcome.filter') }}
            </d-button>

            <d-button
              theme="danger"
              outline
              @click.prevent="resetFilters"
              class="ml-2">
              {{ $t('welcome.clear') }}
            </d-button>
          </div>
        </d-col>
      </d-row>
    </d-modal-body>
  </d-modal>
</template>

<script>
import CustomerPanel from './../../mixins/CustomerPanel.vue';
import debounce from 'lodash.debounce';
import FormUtils from './../../utils/FormUtils.js';
import HasPagination from './../../mixins/HasPagination.vue';
import ListUtils from './../../utils/ListUtils.js';
import Pagination from './../../components/common/Pagination.vue';
import CustomDecimals from './../../utils/CustomDecimals.js';
import Vue from 'vue';

export default {
  name: 'products-search-popup',
  props: {
    currencyId: {
      default: 0,
      type: Number
    },
    productsDiscount: {
      default: 0,
      type: Number
    },
    productUuid: {
      default: '',
      type: String
    },
    selectedCurrencySymbol: {
      default: '',
      type: String
    },
    tagCategories: {
      default: () => ([]),
      type: Array
    },
    selectedTagCategory: {
      default: null
    },
    orderCreatorMode: {
      default: false,
      type: Boolean
    },
    saveFilters: {
      default: false,
      type: Boolean
    },
    cartEnabled: {
      default: true,
      type: Boolean
    }
  },
  mixins: [
    CustomerPanel,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    currencyNames () {
      let output = {};

      for (let i = 0; i < this.currencies.length; i++) {
        output[this.currencies[i].id] = this.currencies[i].code;
      }

      return output;
    },
    mainTags () {
      return this.tagCategories.filter(tagCategory => !!tagCategory.is_main);
    },
    filteredTagCategories () {
      let mainTag = this.filters.main_tag;

      if (mainTag) {
        let mainTagData = this.mainTags.filter(tagCategory => tagCategory.id === mainTag);

        if (mainTagData && mainTagData[0]) {
          let mainTagChildren = mainTagData[0].children.map(children => children.id);
          return this.tagCategories.filter(tagCategory => !tagCategory.is_main).filter(tagCategory => mainTagChildren.indexOf(tagCategory.id) > -1);
        }
      }

      return this.tagCategories.filter(tagCategory => !tagCategory.is_main);
    }
  },
  data () {
    return {
      foundedProducts: [],
      filters: {
        is_sale: false,
        search: '',
        availability_from: '',
        price_from: '',
        price_to: '',
        order_by: 'price',
        order_dir: 'ASC',
        tags: {},
        main_tag: null,
        tagOperators: {}
      },
      activeTab: 'filters',
      cart: [],
      currencies: [],
      loadedCurrencies: false,
      showAllCurrencies: false,
      loadError: false,
      loaded: false
    };
  },
  watch: {
    'filters.main_tag': {
      handler () {
        this.debouncedLoadFilteredData();
      },
      deep: true
    },
    'filters.tags': {
      handler () {
        this.debouncedLoadFilteredData();
      },
      deep: true
    },
    'filters.tagOperators': {
      handler () {
        this.debouncedLoadFilteredData();
      },
      deep: true
    },
    'filters.order_by': {
      handler (newValue) {
        if (newValue === 'availability') {
          this.filters.order_dir = 'DESC';
        }
      }
    }
  },
  mounted () {
    let savedConfig = localStorage.getItem('headwear-crm-products-search-popup-filters');

    if (this.currencyId === -1) {
      this.showAllCurrencies = true;
    }

    if (this.saveFilters && savedConfig) {
      savedConfig = JSON.parse(savedConfig);

      if (typeof savedConfig.is_sale !== 'undefined') {
        this.filters.is_sale = !!savedConfig.is_sale;
      } else {
        this.filters.is_sale = false;
      }

      this.filters.search = savedConfig.search;
      this.filters.availability_from = savedConfig.availability_from;
      this.filters.price_to = savedConfig.price_to;
      this.filters.price_from = savedConfig.price_from;
      this.filters.order_by = savedConfig.order_by || 'price';
      this.filters.order_dir = savedConfig.order_dir || 'ASC';
      this.filters.tags = JSON.parse(JSON.stringify(savedConfig.tags));
      this.filters.main_tag = JSON.parse(JSON.stringify(savedConfig.main_tag));

      if (savedConfig.tagOperators) {
        this.filters.tagOperators = JSON.parse(JSON.stringify(savedConfig.tagOperators));
      }
    } else {
      this.filters.is_sale = false;
      this.filters.search = '';
      this.filters.availability_from = '';
      this.filters.price_to = '';
      this.filters.price_from = '';
      this.filters.order_by = 'price';
      this.filters.order_dir = 'ASC';
      this.resetTagFilters();

      if (this.selectedTagCategory && parseInt(this.selectedTagCategory, 10)) {
        Vue.set(this.filters, 'main_tag', this.selectedTagCategory);
      } else {
        Vue.set(this.filters, 'main_tag', null);
      }
    }

    this.loadCurrencies();
    this.loadFilteredData();
  },
  methods: {
    addProduct (productID, qty) {
      let productName = this.foundedProducts.filter(product => product.id === productID)[0].name;
      let productImageURL = this.foundedProducts.filter(product => product.id === productID)[0].image_url;
      let productExistsInCart = this.cart.filter(product => product.product_id === productID);

      if (productExistsInCart && productExistsInCart.length) {
        productExistsInCart[0].qty += qty;
      } else {
        this.cart.push({
          product_id: productID,
          product_name: productName,
          product_image_url: productImageURL,
          qty
        });
      }

      this.activeTab = 'cart';
    },
    addProductToOrder (productID) {
      this.$bus.$emit('products-search-popup-select-product', {
        id: productID,
        uuid: this.productUuid
      });
      this.handleClose();
    },
    addToOrder () {
      this.$store.commit('setCart', this.cart);
      this.$router.push('/zamowienia/nowe/z-koszyka');
    },
    deleteFromCart (index) {
      this.cart.splice(index, 1);
    },
    handleClose () {
      this.$bus.$emit('products-search-popup-close');
    },
    cancel () {
      this.handleClose();
    },
    getProductPrice (product) {
      let foundedPrice = product.prices.filter(price => price.currency_id === this.currencyId);

      if (foundedPrice.length) {
        return Number(foundedPrice[0].price).toFixed(2) + ' ' + this.selectedCurrencySymbol;
      }

      return '';
    },
    getProductDiscountedPrice (product) {
      let foundedPrice = product.prices.filter(price => price.currency_id === this.currencyId);

      if (foundedPrice.length) {
        return CustomDecimals.roundProperly(foundedPrice[0].price - ((this.productsDiscount / 100) * foundedPrice[0].price)) + ' ' + this.selectedCurrencySymbol;
      }

      return '';
    },
    loadCurrencies () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-panel/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 500),
    loadFilteredData () {
      if (this.saveFilters) {
        localStorage.setItem('headwear-crm-products-search-popup-filters', JSON.stringify(this.filters));
      }

      let tagFilters = [];
      let tagFiltersKeys = Object.keys(this.filters.tags);
      this.loaded = false;

      for (let i = 0; i < tagFiltersKeys.length; i++) {
        let tagID = tagFiltersKeys[i];

        if (this.filters.tags && this.filters.tags[tagID] && this.filters.tags[tagID].length) {
          let tagOperator = 'OR';

          if (this.filters.tagOperators[tagID] === true) {
            tagOperator = 'AND';
          }

          tagFilters.push({
            id: tagID,
            values: this.filters.tags[tagID].slice(),
            operator: tagOperator
          });
        }
      }

      if (this.filters.main_tag) {
        let mainTagData = this.mainTags.filter(tagCategory => tagCategory.id === this.filters.main_tag);

        if (mainTagData && mainTagData[0]) {
          tagFilters.push({
            id: (this.filters.main_tag).toString(),
            values: [mainTagData[0].tags[0].id],
            operator: 'AND'
          });
        }
      }

      let where = {
        is_sale: +this.filters.is_sale,
        search: this.filters.search,
        availability_from: parseInt(this.filters.availability_from, 10),
        price_from: this.filters.price_from,
        price_to: this.filters.price_to,
        order_by: this.filters.order_by,
        order_dir: this.filters.order_dir,
        tags: tagFilters
      };

      if (this.orderCreatorMode) {
        where.customer_to_prices = {
          currency_id: this.currencyId,
          discount_products: this.productsDiscount
        };
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: this.getApiEndpoint('/products/items?page=' + this.currentPage + '&lang=' + this.$store.getters.getUserLang),
        listField: 'foundedProducts',
        params: {
          where,
          with: ['productPrices', 'images']
        },
        successAction: () => {
          this.foundedProducts.map(product => product.prices.sort((priceA, priceB) => priceB.id - priceA.id));
          this.foundedProducts = this.foundedProducts.map(product => {
            product.qty = 1;
            return product;
          });
        }
      });
    },
    resetTagFiltersWithoutMain () {
      let availableFilters = this.tagCategories.map(tagCategory => tagCategory.id);
      let excludedFromClear = this.filteredTagCategories.map(filter => filter.id);

      for (let i = 0; i < availableFilters.length; i++) {
        let id = availableFilters[i];

        if (excludedFromClear.indexOf(id) === -1) {
          Vue.set(this.filters.tags, id, null);
          Vue.set(this.filters.tagOperators, id, null);
        }
      }
    },
    resetTagFilters () {
      Vue.set(this.filters, 'main_tag', null);
      let availableFilters = this.tagCategories.map(tagCategory => tagCategory.id);

      for (let i = 0; i < availableFilters.length; i++) {
        let id = availableFilters[i];
        Vue.set(this.filters.tags, id, null);
        Vue.set(this.filters.tagOperators, id, null);
      }
    },
    resetFilters () {
      this.filters.is_sale = false;
      this.filters.search = '';
      this.filters.availability_from = '';
      this.filters.price_to = '';
      this.filters.price_from = '';
      this.filters.order_by = 'price';
      this.filters.order_dir = 'ASC';
      this.resetTagFilters();
      this.$bus.$emit('pagination-reset', false);
      this.loadFilteredData();

      if (this.saveFilters) {
        localStorage.removeItem('headwear-crm-products-search-popup-filters');
      }
    },
    onEnter () {
      this.debouncedLoadFilteredData();
    },
    isInCart (productID) {
      let existingItems = this.cart.filter(product => product.product_id === productID);
      return existingItems && existingItems.length;
    },
    countInCart (productID) {
      let existingItems = this.cart.filter(product => product.product_id === productID);

      if (existingItems[0]) {
        return existingItems[0].qty;
      }

      return 0;
    },
    resetCart () {
      Vue.set(this, 'cart', []);
    },
    showContainerDeliveries (product) {
      this.$bus.$emit('container-preview-popup-open', parseInt(product.id, 10), product.code);
    },
    selectText (e) {
      e.target.select();
    }
  }
}
</script>

<style lang="scss" scoped>
.modal /deep/ .modal-dialog {
  max-width: 1200px;
  width: 100%;

  .modal-body {
    height: 90vh;
  }
}

.modal-header {
  border-bottom: 0;
}

.modal /deep/ .v-select .vs__search {
  margin-bottom: 0!important;
}

.search-result-product-image {
  width: 100%;
}

.popup-tabs {
  background: #fff;
  margin-top: -30px;
}

.product {
  &-image {
    height: auto;
    margin: 0 auto 10px auto;
    max-height: 120px;
    max-width: 120px;
    width: auto;
  }

  &-empty-image {
    align-items: center;
    background: #e5e5e5;
    color: #fff;
    display: flex;
    font-size: 48px;
    height: 120px;
    justify-content: center;
    margin: 0 auto 10px auto;
    width: 120px;
  }
}

.filter-sidebar {
  background: #fff;
  border-left: 1px solid #dfe1e3;
  left: 0;
  margin: -2rem 0 0 0;
  padding: 2rem 0 0 0;
  position: relative;

  .filters-values-wrapper {
    height: calc(90vh - 94px);
    overflow: auto;
  }

  /deep/ .vs__dropdown-toggle {
    max-height: none!important;
  }

  /deep/ .vs__selected-options {
    height: auto!important;
    min-height: 22px!important;
    padding: 5px!important;
  }

  /deep/ .vs__selected {
    background: #fff;
    border-color: #007bff;
    color: #007bff;
    margin: .25rem!important;
  }

  /deep/ .vs__deselect {
    fill: var(--danger);
    opacity: .5;
  }
}

.filters-values {
  padding-bottom: 40px;
}

.filters-actions {
  background: #fff;
  border-top: 1px solid #dfe1e3;
  margin: 0;
  padding: 20px 20px 0 20px;
  position: sticky;
  text-align: center;
  top: calc(100% - 54px);
}

.deliveries-link {
  align-items: center;
  display: inline-flex;
  font-size: .75rem;
  line-height: 1.2;

  .material-icons {
    font-size: 14px;
    margin-right: 2px;
  }
}

.founded-products {
  height: 90vh;
  margin-top: -2rem;
  padding-top: 2rem;
  overflow: auto;

  &.is-loading {
    pointer-events: none;

    & > * {
      opacity: .25;
    }
  }
}

.cart-content {
  list-style-type: none;
  padding: 0;

  &-item {
    align-items: center;
    border: 1px solid #ddd;
    border-radius: 5px;
    display: flex;
    margin-bottom: 10px;
    padding: 10px;

    &-name {
      width: calc(100% - 100px);
    }

    &-actions {
      width: 100px;

      span,
      small {
        line-height: 1;
      }

      small {
        font-weight: bold;
      }

      span {
        cursor: pointer;
        margin-top: -4px;
      }
    }
  }
}

.modal-header .close {
    margin: -1.5rem -2rem -1rem auto;
}
</style>
